import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { CONNECT_PATH, getPendingOAuthRequest } from '../services/oauthService';

/** The `state.from` a guard stashes when it bounces someone to /login. */
type FromLocation = { pathname?: string; search?: string; hash?: string };

/**
 * Where to send a user who has just signed in (or already was) on an auth
 * page. A pending OAuth consent (#272) wins: it was started from an app that
 * is waiting on the user; the page reads the id from sessionStorage, so it is
 * never put back in the URL (analytics would record it). Otherwise `from` —
 * path AND query string, so deep links such as `/workspace?view=…` survive —
 * unless it is itself an auth page (that would loop). `null` = landing view.
 */
export const postLoginTarget = (from?: FromLocation | null): string | null => {
  if (getPendingOAuthRequest()) return CONNECT_PATH;
  const pathname = from?.pathname;
  if (!pathname || isAuthRoute(pathname)) return null;
  return `${pathname}${from?.search ?? ''}${from?.hash ?? ''}`;
};

/**
 * Whether a guard over a sign-in form should hold its spinner: during the
 * first session check, and once a sign-in has succeeded but the profile (whose
 * account type picks the landing route) is still loading. NOT while a form's
 * own call is being checked: swapping the form out remounts it and drops its
 * state, such as an invited teammate's "Set New Password" step (#395).
 */
export const isSessionSettling = ({
  isInitializing,
  isAuthenticated,
  isLoading,
}: {
  isInitializing: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
}): boolean => isInitializing || (isAuthenticated && isLoading);

/** Where an authenticated account belongs, by type. */
export const landingRouteFor = (accountType?: 'client' | 'consumer'): string =>
  accountType === 'consumer' ? '/vault' : '/workspace';

interface PublicOnlyRouteProps {
  children: React.ReactNode;
}

/**
 * The inverse of `ProtectedRoute`: renders children only for signed-out users.
 *
 * Amplify v6 refuses to `signIn()` over a live session — it throws
 * `UserAlreadyAuthenticatedException` rather than silently swapping users the
 * way v5 did. Without this guard a signed-in user reaching /login by bookmark,
 * back button or the landing site's "Sign in" link gets the form, submits it,
 * and is met with "There is already a signed in user" (#261).
 *
 * Honours `location.state.from` (path + query) so a user who was bounced to
 * /login by `ProtectedRoute` and then authenticated in another tab lands back
 * where they were trying to go — and a pending OAuth consent before either.
 */
export const PublicOnlyRoute: React.FC<PublicOnlyRouteProps> = ({ children }) => {
  const { isAuthenticated, userProfile, isLoading, isInitializing } = useAuth();
  const location = useLocation();

  if (isSessionSettling({ isInitializing, isAuthenticated, isLoading })) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    const from = (location.state as { from?: FromLocation } | null)?.from;
    const target = postLoginTarget(from) ?? landingRouteFor(userProfile?.accountType);
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
};

const AUTH_ROUTES = ['/login', '/register', '/forgot-password'];

export const isAuthRoute = (pathname: string): boolean =>
  AUTH_ROUTES.some(route => pathname === route || pathname.startsWith(`${route}/`));

export default PublicOnlyRoute;
